import React from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ChartTooltip,
  ChartContainer,
} from "@/components/ui/chart";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useDashboardStats } from "@/hooks/useDashboardStats";
import { Skeleton } from "@/components/ui/skeleton";

const COLORS = {
  Audiência: "#072a45",
  Prazo: "#dc2626",
  Reunião: "#16a34a",
  Despacho: "#0ea5e9", // cor exclusiva
  Outro: "#6b7280",
};

const ALL_TYPES = ["Audiência", "Reunião", "Prazo", "Despacho", "Outro"];

export const DashboardStats: React.FC = () => {
  const { stats, isLoading } = useDashboardStats();

  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2">
        <Card className=" dark:bg-white">
          <CardHeader>
            <CardTitle className="dark:text-gray-600">Distribuição por Tipo</CardTitle>
          </CardHeader>
          <CardContent className="w-full">
            <Skeleton className="h-64 w-full" />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="dark:text-gray-600">Compromissos Semanais</CardTitle>
          </CardHeader>
          <CardContent className="w-full">
            <Skeleton className="h-64 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  // Garante que todos os tipos existam
  const chartData = ALL_TYPES.map((type) => {
    const found = stats?.appointmentsByType?.find(
      (item) => item.type.trim().toLowerCase() === type.toLowerCase()
    );
    return {
      type,
      count: found?.count ?? 0,
    };
  });

  const weeklyData =
    stats?.weeklyStats?.length > 0
      ? stats.weeklyStats
      : [
          { week: "Sem 1", count: 0 },
          { week: "Sem 2", count: 0 },
          { week: "Sem 3", count: 0 },
          { week: "Sem 4", count: 0 },
        ];

  const hasData = chartData.some((item) => item.count > 0);
  const hasWeeklyData = weeklyData.some((item) => item.count > 0);

  return (
    <div className="grid gap-4 md:grid-cols-2">
      {/* Distribuição por Tipo */}
      <Card className=" dark:bg-white">
        <CardHeader>
          <CardTitle className="text-lg dark:text-gray-600">Distribuição por Tipo</CardTitle>
        </CardHeader>
        <CardContent>
          {hasData ? (
            <div className="w-full" style={{ aspectRatio: "2 / 1" }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="count"
                    label={({ type, count }) =>
                      count > 0 ? `${type}: ${count}` : null
                    }
                  >
                    {chartData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={
                          COLORS[entry.type.trim() as keyof typeof COLORS] ||
                          COLORS.Outro
                        }
                      />
                    ))}
                  </Pie>
                  <ChartTooltip
                    content={({ active, payload }) => {
                      if (active && payload && payload.length > 0) {
                        const data = payload[0].payload;
                        return (
                          <div className="bg-white p-2 border rounded shadow">
                            <p className="font-medium">{data.type}</p>
                            <p className="text-sm">
                              {data.count} compromissos
                            </p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Legend
                    verticalAlign="bottom"
                    content={() => (
                      <ul className="flex flex-wrap gap-4 justify-center text-sm">
                        {chartData.map((entry, index) => (
                          <li key={`item-${index}`} className="flex items-center gap-1">
                            <span
                              className="w-3 h-3 inline-block rounded-full"
                              style={{
                                backgroundColor:
                                  COLORS[entry.type.trim() as keyof typeof COLORS] || COLORS.Outro,
                              }}
                            />
                            {entry.type}: {entry.count}
                          </li>
                        ))}
                      </ul>
                    )}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">
              <div className="text-center">
                <p className="text-sm">Nenhum compromisso encontrado</p>
                <p className="text-xs text-gray-400">
                  Crie seus primeiros compromissos para ver os gráficos
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Compromissos Semanais */}
      <Card className=" dark:bg-white">
        <CardHeader>
          <CardTitle className="text-lg dark:text-gray-600">Compromissos Semanais</CardTitle>
        </CardHeader>
        <CardContent>
          {hasWeeklyData ? (
            <div className="w-full" style={{ aspectRatio: "2 / 1" }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={weeklyData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <XAxis
                    dataKey="week"
                    tick={{ fontSize: 12 }}
                    axisLine={{ stroke: "#e5e7eb" }}
                  />
                  <YAxis
                    tick={{ fontSize: 12 }}
                    axisLine={{ stroke: "#e5e7eb" }}
                    tickLine={{ stroke: "#e5e7eb" }}
                  />
                  <Bar
                    dataKey="count"
                    fill="#072a45"
                    radius={[4, 4, 0, 0]}
                    maxBarSize={60}
                  />
                  <ChartTooltip
                    content={({ active, payload, label }) => {
                      if (active && payload && payload.length > 0) {
                        return (
                          <div className="bg-white p-2 border rounded shadow">
                            <p className="font-medium">{label}</p>
                            <p className="text-sm">
                              {payload[0].value} compromissos
                            </p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">
              <div className="text-center">
                <p className="text-sm">Nenhum dado semanal disponível</p>
                <p className="text-xs text-gray-400">
                  Crie compromissos para ver as estatísticas semanais
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
